// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-10-13 20:05:51 +0200 (Tue, 13 Oct 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 166532 $
//  Purpose   : uix root reducers
// =============================================================================
// ---Third-party imports
import { handleAction as reduxHandleAction } from 'redux-actions';
import { combineReducers } from 'redux';
import { createTransform } from 'redux-persist';
// ---Framework imports
import { has, dict, get, isObject, isNil, mapItems, filterItems } from 'ldash';
import log from 'uix/logging';
// ---Local imports
import { Reducers } from './registries';
import { resetGenerators } from './actions';
import { persistReducer, readFailHandler } from './persist/redux';
import { hackApiUrl } from './api';
import { uiReducer } from './persist/ui';
import * as migrations from './migrate';
import { reduceMergeReducers } from 'uix/utils/reducers';
// ---Globals
let packageReducers = dict(mapItems(Reducers, (section, reducers)=>[
        `${section}Reducer`,
        reduceMergeReducers(...reducers)
    ]));
log.info('setup core reducers');
const initialConfigState = {
    apiurl: `http://127.0.0.1:${process.env.VLS_TEST_PORT || 80}`,
    // same shape as the setAppConfig result (see config/actions.js), so config
    // consumers survive the window where INIT_APP clears the config
    domains: {
        data: {},
        names: [],
        ghostNames: [],
        prefix2name: {},
        name2prefix: {},
        odid2name: {},
        otid2name: {},
        rootDomain: null
    },
    // contains custom overwrites (like presentations that map stuff to exhibition/view)
    routingBeautify: [],
    // contains selected data from server.ini + itypes
    instance: {
        itypes: {
            byId: {},
            byName: {},
            datatypeMap: {},
            datatypeMapInverse: {}
        },
        i18n: {},
        cacheKeys: {}
    },
    ddata: {},
    ddataCache: {},
    deploy: {},
    ...packageReducers.configReducer(undefined, 'INITIAL_DATA_DUMMY')
};
let configReducer = (config = initialConfigState, action)=>{
    config = packageReducers.configReducer(config, action);
    let payload = action.payload;
    switch(action.type){
        case 'SET_APP_CONFIG':
            {
                return {
                    ...config,
                    ...payload.config,
                    instance: {
                        ...config.instance,
                        ...payload.config.instance,
                        cacheKeys: payload.config.instance?.cacheKeys || {}
                    }
                };
            }
        case 'SET_APP_STATE':
            {
                if (payload.apiurl) {
                    config = {
                        ...config,
                        apiurl: payload.apiurl
                    };
                }
                if (payload.state == 'INIT_APP') {
                    // major cleanup
                    // XXX: we will get some invalid config access during logout/login (react-redux 7.0.2?) - so maybe it's better to skip the cleanup
                    // the above should be solved now by clearing with initialConfigState (instead of empty `instance` dict)
                    // but keep cached i18n layers/cache keys
                    return {
                        ...config,
                        ...initialConfigState,
                        ddataCache: config.ddataCache,
                        instance: {
                            ...initialConfigState.instance,
                            i18n: config.instance.i18n,
                            cacheKeys: config.instance.cacheKeys
                        }
                    };
                }
                return config;
            }
        case 'SET_DEPLOY_CONFIG':
            {
                let apiurl = '';
                if (payload.mode != 'vls') {
                    apiurl = payload.apiurl || config.apiurl;
                    hackApiUrl(apiurl);
                }
                return {
                    ...config,
                    deploy: payload,
                    apiurl
                };
            }
        case 'SERVER_DIRTY_CONFIGS':
            {
                return {
                    ...config,
                    dconfigDirty: payload.dirty
                };
            }
        default:
            return config;
    }
};
const initialMetaState = {
    // contains decoded url
    // {dname, cname, classname, tabname, ctxid, id, query, pathname, ...query}
    request: {},
    // contains replaced query params (url updates without browser navigation)
    replacedQueryParams: {},
    // state transitions:
    // INIT_START -> INIT_APP -> APP_RUNNING -> FATAL_ERROR (saga source, error overlay with page reload)
    //                                          -> INIT_START
    //                                       -> API_OFFLINE (<Link/> and disable go(), dbgfooter test with Alt-KeyO / alt+shift+o)
    //                                          -> APP_RUNNING (on SERVER/STARTUP, onNetworkActivity via auth/info)
    //                                       -> VIEW_UNAUTHORIZED (on session expiration, show login dialog, disable visible background)
    //                                          -> INIT_APP (on relogin with changed username)
    //                                          -> APP_RUNNING (on relogin with unchanged username)
    //                                       -> API_SWITCH (dev) -> INIT_APP
    //                        -> INIT_APP_ERROR (Fullscreen ErrorMsg (prod) or ServerChooser (dev))
    //                           - timeout or other error on apiurl
    //                        -> INIT_APP_404 (Fullscreen ErrorMsg (prod+dev))
    //                           - route not registered in views.js
    state: 'INIT_START',
    prevState: null,
    deployMode: null,
    loginFailure: null,
    window: {
        visible: true
    },
    graphics: {},
    ...packageReducers.metaReducer(undefined, 'INITIAL_DATA_DUMMY')
};
let metaReducer = (meta = initialMetaState, action)=>{
    meta = packageReducers.metaReducer(meta, action);
    const payload = action.payload;
    switch(action.type){
        case 'ERROR_SAGAS':
            {
                // this is terminal
                return {
                    ...meta,
                    state: 'FATAL_ERROR'
                };
            }
        case 'SET_APP_STATE':
            {
                let appstate = payload.state;
                if ([
                    'INIT_APP_ERROR',
                    'INIT_APP_404',
                    'API_SWITCH'
                ].includes(appstate)) {
                    meta = {
                        ...initialMetaState,
                        prev_request: meta.request
                    };
                    if (appstate == 'INIT_APP_404') {
                        meta = {
                            ...meta,
                            router404: payload.router404
                        };
                    }
                }
                return {
                    ...meta,
                    state: payload.state,
                    prevState: meta.state
                };
            }
        case 'SET_GRAPHICS_LOADED':
            {
                return {
                    ...meta,
                    graphics: {
                        ...meta.graphics,
                        ...dict(payload.name2data.map(([name, data])=>[
                                name,
                                data
                            ]))
                    }
                };
            }
        case 'SET_WINDOW_VISIBLE':
            {
                return {
                    ...meta,
                    window: {
                        ...meta.window,
                        visible: payload.visible
                    }
                };
            }
        default:
            {
                return meta;
            }
    }
};
const initialDataState = {
    runId: 0,
    apiResult: {},
    apiPartsReload: {},
    ...packageReducers.dataReducer(undefined, 'INITIAL_DATA_DUMMY')
};
let dataReducer = (data = initialDataState, action)=>{
    data = packageReducers.dataReducer(data, action);
    let payload = action.payload;
    switch(action.type){
        case 'SET_INITIAL_STATE':
            {
                if (payload.section == 'data') {
                    return initialDataState;
                } else {
                    return data;
                }
            }
        case 'SET_API':
            {
                let { key, result, apiId, runId } = payload;
                return {
                    ...data,
                    apiResult: {
                        ...data.apiResult,
                        [key]: {
                            ...result,
                            apiId,
                            runId
                        }
                    }
                };
            }
        case 'CLEAR_API_RESULT':
            {
                let apiResult = dict(filterItems(data.apiResult, (key)=>key !== payload.key));
                return {
                    ...data,
                    apiResult
                };
            }
        case 'REDUCE_API_RESULT':
            {
                // if reducer
                let result = null;
                try {
                    result = payload.reducer(data, action.payload.data, action.payload.error);
                } catch (e) {
                    log.exception('error during reducer: %o', e);
                    return data;
                }
                if (!isObject(result)) {
                    if (!isNil(result)) {
                        log.warn('reducer function should return a object that can be merged into state or null/undefined');
                    }
                    return data;
                }
                return {
                    ...data,
                    ...result
                };
            }
        default:
            return data;
    }
};
const initialDebugState = {
    errors: [],
    isThemeEditorOn: false,
    ...packageReducers.debugReducer(undefined, 'INITIAL_DEBUG_DUMMY')
};
let debugReducer = (debug = initialDebugState, action)=>{
    debug = packageReducers.debugReducer(debug, action);
    let { type, payload } = action;
    if (type == 'persist/REHYDRATE' && action.err) {
        readFailHandler(action.key);
    }
    if (type == 'ERROR_API' || type == 'ERROR_SERVER' || type == 'ERROR_SAGAS') {
        return {
            ...debug,
            errors: [
                payload,
                ...debug.errors
            ].slice(0, 15)
        };
    }
    return debug;
};
let initialState = {
    data: initialDataState,
    meta: initialMetaState,
    config: initialConfigState,
    debug: initialDebugState
};
let enhancedActionReducer = (reducer)=>{
    let wrapped = (state = initialState, action)=>{
        /* break up isolation for actions
    we can flag our own actions via action.meta for this behavior
    XXX: a sane alternative is to use a root reducer
    */ if (action.type === 'BATCH_ACTIONS') {
            return action.payload.reduce(wrapped, state);
        }
        let moreStorePaths = get(action, 'meta.moreStorePaths');
        if (moreStorePaths) {
            action.more = dict(moreStorePaths.map((path)=>[
                    path,
                    ()=>get(state, path)
                ]));
        }
        return reducer(state, action);
    };
    return wrapped;
};
export const buildReducers = (otherReducers)=>{
    //~ let compressor = createCompressor()
    let transforms = [] // [compressor]
    ;
    let persistInstanceSubset = createTransform((inboundState)=>{
        let instance = inboundState.instance || {};
        return {
            ...inboundState,
            instance: {
                i18n: instance.i18n || {},
                cacheKeys: instance.cacheKeys || {}
            }
        };
    }, (outboundState)=>{
        let instance = outboundState && outboundState.instance || {};
        return {
            ...outboundState,
            instance: {
                ...initialConfigState.instance,
                i18n: instance.i18n || {},
                cacheKeys: instance.cacheKeys || {}
            }
        };
    }, {
        whitelist: [
            'uix_config'
        ]
    });
    let configTransforms = [
        ...transforms,
        persistInstanceSubset
    ];
    let reducers = combineReducers({
        data: persistReducer(dataReducer, {
            key: 'uix_data',
            //~ scope: 'session',
            whitelist: [
                'history'
            ],
            transforms
        }),
        meta: persistReducer(metaReducer, {
            key: 'uix_meta',
            whitelist: [
                'progressHistory',
                'contextHelp'
            ],
            ...migrations.meta
        }),
        config: persistReducer(configReducer, {
            key: 'uix_config',
            whitelist: [
                'apiurl',
                'ddataCache',
                'instance'
            ],
            transforms: configTransforms
        }),
        ui: persistReducer(uiReducer, {
            key: 'uix_ui'
        }),
        debug: debugReducer,
        ...otherReducers
    });
    reducers = reduceMergeReducers(reducers, ...Reducers.root);
    reducers = enhancedActionReducer(reducers);
    //~ reducers = persistReducer(reducers, {key: 'uix_ui', whitelist: ['ui']})
    return reducers;
};
